import { API_BASE_URL } from "../constants";
import type { PermissionLevel } from "./adminService";

/**
 * Member shape returned by GET /team/members. Mirrors `Member` in
 * adminService.ts (both are projections of the same server-side
 * `MemberSummary`/`toMemberSummary`), but this listing is scoped to the
 * calling manager's own associated members in their own organization — never
 * cross-org, unlike the admin console.
 */
export interface TeamMember {
  member_id: string;
  organization_id: string;
  email_address: string;
  name: string;
  status: string;
  level: PermissionLevel;
}

/**
 * Envelope returned by GET /team/members. Unlike the admin listing, this is
 * not cursor-paginated and carries no `organizations` map — every row
 * belongs to the caller's own organization, so there is nothing to look up.
 */
export interface ListTeamMembersResponse {
  members: TeamMember[];
  results_metadata: {
    total: number;
  };
}

function authHeaders(token?: string): HeadersInit {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * GET /team/members
 *
 * Members associated with the calling manager. Server-side (TeamService)
 * scopes this to the manager's own associations, not their whole
 * organization.
 */
export async function listTeamMembers(
  token?: string,
  signal?: AbortSignal,
): Promise<ListTeamMembersResponse> {
  const response = await fetch(`${API_BASE_URL}/team/members`, {
    headers: authHeaders(token),
    signal,
  });
  if (!response.ok) {
    throw new Error(await response.text());
  }
  return response.json();
}

/**
 * POST /team/members
 *
 * Invite a new member by email. Role and target organization are forced
 * server-side (stytch_member, the manager's own org) — there is nothing else
 * to send.
 */
export async function inviteTeamMember(
  email: string,
  token?: string,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/team/members`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    throw new Error(await response.text());
  }
}

/**
 * DELETE /team/members/:stytchMemberId
 *
 * Removes the (manager, member) association only — the underlying Stytch
 * member account is never touched. The member keeps their account and any
 * access other managers have granted them.
 */
export async function removeTeamMember(
  memberId: string,
  token?: string,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/team/members/${encodeURIComponent(memberId)}`,
    {
      method: "DELETE",
      headers: authHeaders(token),
    },
  );
  if (!response.ok) {
    throw new Error(await response.text());
  }
}
